import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SendResult } from '@/api/types';
import { useApi } from '@/api/provider';
import { queryKeys } from '@/api/queryKeys';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { createLeaderElection, LEADER_CHANNEL_NAME } from '@/realtime/leader';
import { showSendNotice } from '@/features/send-undo/pendingSend';
import { sendOutcome } from '@/features/send-undo/sendOutcome';
import { noteSendResult } from '@/features/sent-copies/useSentCopies';
import { flushOutbox } from './flush';
import {
  failedCount,
  outboxOwner,
  outboxSnapshot,
  recoverOutbox,
  subscribeOutbox,
  waitingCount,
} from './store';
import type { OutboxMessage } from './types';

/** The whole queue, oldest first. Re-renders on any change, in any tab. */
export function useOutbox(): readonly OutboxMessage[] {
  return useSyncExternalStore(subscribeOutbox, outboxSnapshot, outboxSnapshot);
}

/** Just the counts, for the banner and the folder row. */
export function useOutboxCounts(): { waiting: number; failed: number } {
  const messages = useOutbox();
  return { waiting: waitingCount(messages), failed: failedCount(messages) };
}

/** The queue for one mailbox. The Outbox view is scoped like every other folder. */
export function useAccountOutbox(accountId: string | undefined): readonly OutboxMessage[] {
  const messages = useOutbox();
  return accountId === undefined
    ? messages
    : messages.filter((one) => one.accountId === accountId);
}

/** How often a leader re-checks the queue: the backoff is in the records. */
const TICK_MS = 15_000;

/**
 * Runs the flush loop in exactly one tab.
 *
 * Mounted once, on the shell. Three things wake it: coming back online, the
 * page becoming visible, and a slow tick that exists only to pick up a backoff
 * that has come due. There is no Background Sync: Safari has none, the service
 * worker in this app registers no `sync` handler, and doc 06 §10 names the
 * page flush as a first-class path rather than a fallback — "in practice this
 * covers most real cases, because users reopen the app".
 */
/**
 * What the flusher does with each message that went. The folder counts move
 * (the message is in Sent, or will be), a kept copy puts the sent-copies list
 * out of date (#84), and a caveat -- refused recipients, a copy to Sent being
 * retried -- is said in the same strip a held send uses, because the composer
 * that queued it closed long ago. A clean send stays quiet: the Outbox going
 * empty already says it, and a "Message sent" for every queued message would be
 * a burst of them.
 */
export function reportOutboxSend(
  queryClient: QueryClient,
  record: OutboxMessage,
  result: SendResult,
): void {
  const keys = queryKeys.account(record.accountId);
  void queryClient.invalidateQueries({ queryKey: keys.folders() });
  void queryClient.invalidateQueries({ queryKey: keys.messagesScope() });
  noteSendResult(queryClient, record.accountId, result);
  const outcome = sendOutcome(record.accountId, result);
  if (outcome !== undefined && outcome.routine !== true) showSendNotice(outcome);
}

export function useOutboxFlusher(): void {
  const api = useApi();
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const leading = useRef(false);
  const running = useRef(false);

  const run = useCallback(async () => {
    if (!leading.current || running.current) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    running.current = true;
    try {
      await flushOutbox(api, {
        shouldContinue: () => leading.current,
        onSent: (record, result) => reportOutboxSend(queryClient, record, result),
      });
    } catch {
      // flushOutbox records every per-message failure on the record itself;
      // anything escaping it is a bug in the loop, not a lost message.
    } finally {
      running.current = false;
    }
  }, [api, queryClient]);

  useEffect(() => {
    const election = createLeaderElection({
      channelName: `${LEADER_CHANNEL_NAME}:outbox`,
      onChange: (isLeader) => {
        leading.current = isLeader;
        if (isLeader) void run();
      },
    });
    const onVisibility = () => {
      election.refresh();
      if (document.visibilityState === 'visible') void run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    const ticker = setInterval(() => void run(), TICK_MS);
    // A tab that died mid-send left a lease behind; clearing it is safe because
    // the idempotency key travelled with the record.
    void recoverOutbox().then(() => run());
    return () => {
      clearInterval(ticker);
      document.removeEventListener('visibilitychange', onVisibility);
      election.stop();
      leading.current = false;
    };
  }, [run]);

  useEffect(() => {
    if (online) void run();
  }, [online, run]);

  // A new owner is a new queue: after a revocation the kept mail becomes
  // visible again when the same person signs back in (ADR 0061 §4), and it
  // should go now rather than on the next tick. The owner is set by the
  // session provider, whose effect runs after this (child) hook's first one.
  const owner = useSyncExternalStore(subscribeOutbox, outboxOwner, outboxOwner);
  useEffect(() => {
    if (owner !== null) void recoverOutbox().then(() => run());
  }, [owner, run]);
}
